/** @forge-header
 * @id          parse
 * @role        Ticker parser orchestrator | prefix-based dispatch to sport parsers
 * @pattern     orchestrator
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/sports/*:parsers | kalshiTicker/parsers/*:parsers | kalshiTicker/helpers:parseFallback
 * @downstream  kalshiTicker/index (barrel) | components/atoms/TickerDisplay
 * @exports     parseKalshiTicker:fn | nflTeamFromCode:fn | nhlTeamFromCode:fn
 * @test        parse.test.ts (26 cases)
 */

import type { ParsedTicker } from './types';
import { parseFallback, nflTeamFromCode, nhlTeamFromCode } from './helpers';
import type { TickerParser } from './helpers';

// Sport parsers
import { parseNbaGame, parseNbaSeason } from './sports/nba';
import { parseWnbaGame, parseWnbaSeason } from './sports/wnba';
import { parseNflGame, parseNfl2Td, parseNflFirstTd, parseSuperBowl } from './sports/nfl';
import { parseNhlGame } from './sports/nhl';
import { parseMlbGame } from './sports/mlb';
import { parseNcaaMbGame, parseNcaaWbGame, parseNcaaGame } from './sports/ncaa';
import { parseCfbGame } from './sports/cfb';
import { parseAtpMatch, parseWtaMatch, parseItfMatch, parseItfwMatch } from './sports/tennis';
import { parseUfcFight } from './sports/ufc';
import { parseBoxing } from './sports/boxing';
import { parseEsportsGame } from './sports/esports';
import { parseCricketMatch } from './sports/cricket';
import { parseSoccerGame, parseSoccerBtts } from './sports/soccer';

// Market type parsers
import {
  parseNbaSpread,
  parseWnbaSpread,
  parseNflSpread,
  parseNhlSpread,
  parseNcaaMbSpread,
  parseNcaaWbSpread,
  parseMlbSpread,
  parseSoccerSpread,
} from './parsers/spreads';
import {
  parseNbaTotal,
  parseWnbaTotal,
  parseNflTotal,
  parseNhlTotal,
  parseNcaaMbTotal,
  parseNcaaWbTotal,
  parseMlbTotal,
  parseSoccerTotal,
} from './parsers/totals';
import { parseSeriesWinner } from './parsers/series';

/**
 * Parsers by prefix — longer prefixes listed first to match correctly
 * (e.g., KXNBAGAME before KXNBA)
 */
const PARSERS: Array<[string, TickerParser]> = [
  // WNBA — listed before NBA. KXW != KXN so prefixes don't collide, but
  // keeping WNBA first matches hydra's matcher order and avoids surprises
  // if anything is ever shortened to a common prefix.
  ['KXWNBASERIES', parseSeriesWinner],
  ['KXWNBASPREAD', parseWnbaSpread],
  ['KXWNBATOTAL', parseWnbaTotal],
  ['KXWNBAGAME', parseWnbaGame],
  ['KXWNBA', parseWnbaSeason],
  // NBA — series prefix listed before KXNBA so playoff tickers don't fall
  // through to the season-prop parser.
  ['KXNBASERIES', parseSeriesWinner],
  ['KXNBASPREAD', parseNbaSpread],
  ['KXNBATOTAL', parseNbaTotal],
  ['KXNBAGAME', parseNbaGame],
  ['KXNBA', parseNbaSeason],
  // NFL
  ['KXNFLSPREAD', parseNflSpread],
  ['KXNFLTOTAL', parseNflTotal],
  ['KXNFLFIRSTTD', parseNflFirstTd],
  ['KXNFL2TD', parseNfl2Td],
  ['KXNFLGAME', parseNflGame],
  ['KXSB', parseSuperBowl],
  // NHL
  ['KXNHLSERIES', parseSeriesWinner],
  ['KXNHLSPREAD', parseNhlSpread],
  ['KXNHLTOTAL', parseNhlTotal],
  ['KXNHLGAME', parseNhlGame],
  // MLB
  ['KXMLBSERIES', parseSeriesWinner],
  ['KXMLBSPREAD', parseMlbSpread],
  ['KXMLBTOTAL', parseMlbTotal],
  ['KXMLBGAME', parseMlbGame],
  // NCAA — longer prefixes first
  ['KXNCAAMBSPREAD', parseNcaaMbSpread],
  ['KXNCAAMBTOTAL', parseNcaaMbTotal],
  ['KXNCAAMBGAME', parseNcaaMbGame],
  ['KXNCAAWBSPREAD', parseNcaaWbSpread],
  ['KXNCAAWBTOTAL', parseNcaaWbTotal],
  ['KXNCAAWBGAME', parseNcaaWbGame],
  ['KXNCAAGAME', parseNcaaGame],
  // College Football
  ['KXNCAAFGAME', parseCfbGame],
  // Tennis (ITF folds into ATP/WTA tabs via marketStore)
  ['KXATPMATCH', parseAtpMatch],
  ['KXWTAMATCH', parseWtaMatch],
  ['KXITFWMATCH', parseItfwMatch],
  ['KXITFMATCH', parseItfMatch],
  // UFC / MMA
  ['KXUFCFIGHT', parseUfcFight],
  // Boxing
  ['KXBOXING', parseBoxing],
  // Esports — one parser for all titles (CS2 / Dota2 / Valorant / LoL)
  ['KXCS2GAME', parseEsportsGame],
  ['KXDOTA2GAME', parseEsportsGame],
  ['KXVALORANTGAME', parseEsportsGame],
  ['KXLOLGAME', parseEsportsGame],
  // Cricket
  ['KXWT20MATCH', parseCricketMatch],
  // Soccer — 3-way moneyline (Home/Away/Draw) grouped via marketStore
  // multi-outcome path; spread/total are 2-way binaries per line (full-game
  // only). SPREAD/TOTAL listed before GAME so the longer-suffix prefixes win
  // (no actual collision — GAME != SPREAD/TOTAL — but matches the file's
  // longest-first convention).
  ['KXINTLFRIENDLYSPREAD', parseSoccerSpread],
  ['KXINTLFRIENDLYTOTAL', parseSoccerTotal],
  ['KXINTLFRIENDLYGAME', parseSoccerGame],
  ['KXUECLSPREAD', parseSoccerSpread],
  ['KXUECLTOTAL', parseSoccerTotal],
  ['KXUECLGAME', parseSoccerGame],
  ['KXWCSPREAD', parseSoccerSpread],
  ['KXWCTOTAL', parseSoccerTotal],
  ['KXWCGAME', parseSoccerGame],
];

/** NBA + WNBA period markets share the full-game ticker shape after the series
 *  prefix — `KXNBA1QTOTAL-26JUN05NYKSAS-3` vs `KXNBATOTAL-26JUN05NYKSAS-3`,
 *  `KXWNBA1HTOTAL-26JUN05CONNCHI-84` vs `KXWNBATOTAL-…`. Without this, the
 *  period prefix falls through the dispatch to the `KXNBA`/`KXWNBA` season-prop
 *  catch-all and parses to garbage (no game ⇒ raw-ticker fallback on the
 *  Positions page, no canonical rollup). Capture group 1 = league prefix. */
const PERIOD_RE = /^(KXW?NBA)(1H|2H|[1-4]Q)(SPREAD|TOTAL|WINNER)(-.*)$/;

/** Parse any Kalshi ticker into structured data */
export function parseKalshiTicker(ticker: string): ParsedTicker {
  const raw = ticker.trim().toUpperCase();

  // Normalize an NBA/WNBA period ticker to its full-game series (same league),
  // parse with the existing parsers, then re-tag the original series + period.
  // The period moneyline (WINNER, 3-way incl. TIE) maps to the GAME parser.
  // Result: same game / date / outcome as the full-game shape (so it rolls up
  // under the canonical game), just annotated with `period` for the side label.
  const periodMatch = raw.match(PERIOD_RE);
  if (periodMatch) {
    const [, league, period, betType, rest] = periodMatch;
    const fullSeries = betType === 'WINNER' ? `${league}GAME` : `${league}${betType}`;
    const parsed = parseKalshiTicker(`${fullSeries}${rest}`);
    return {
      ...parsed,
      raw,
      series: `${league}${period}${betType}`,
      period: period as ParsedTicker['period'],
    };
  }

  // Soccer Game Lines: period (1H/2H) spread/total/moneyline + BTTS (any scope).
  // Period spread/total/moneyline normalize to the full-game soccer parser and
  // re-tag the period (bare KXWC1H/KXWC2H ⇒ the GAME parser); BTTS parses
  // directly (no full-game equivalent). Full-game spread/total/moneyline (no
  // period, not BTTS) are NOT matched here — they fall through to PARSERS below.
  const socMatch = raw.match(/^(KX(?:WC|UECL|INTLFRIENDLY))(1H|2H)?(SPREAD|TOTAL|BTTS)?-(.*)$/);
  if (socMatch && (socMatch[2] || socMatch[3] === 'BTTS')) {
    const [, league, period, betType, rest] = socMatch;
    const per = period as ParsedTicker['period'] | undefined;
    if (betType === 'BTTS') {
      return parseSoccerBtts(raw, rest, per);
    }
    const fullSeries = betType ? `${league}${betType}` : `${league}GAME`;
    const parsed = parseKalshiTicker(`${fullSeries}-${rest}`);
    return {
      ...parsed,
      raw,
      series: `${league}${period ?? ''}${betType ?? ''}`,
      period: per,
    };
  }

  for (const [prefix, parser] of PARSERS) {
    if (raw.startsWith(prefix)) {
      return parser(raw);
    }
  }

  return parseFallback(raw);
}

import { detectVenue, parsePolymarketTicker } from './polymarket';

/**
 * Venue-agnostic ticker parser. Detects venue from the prefix and
 * dispatches to the appropriate sub-parser. Always returns a
 * `ParsedTicker` (never null) so callers can use a single render path.
 */
export function parseTicker(ticker: string): ParsedTicker {
  return detectVenue(ticker) === 'polymarket'
    ? parsePolymarketTicker(ticker)
    : parseKalshiTicker(ticker);
}

export { nflTeamFromCode, nhlTeamFromCode };
export { detectVenue, parsePolymarketTicker } from './polymarket';
